import 'react-day-picker/dist/style.css'
import './daypicker.scss'

import { Box, Popover } from '@mui/material'
import { CaretDownIcon } from 'assets/icons'
import { useAppSettings } from 'components/providers/AppSettingsProvider'
import { useCalendar } from 'components/providers/CalendarProvider'
import { endOfWeek, isSameMonth, startOfWeek } from 'date-fns'
// import the locale object
import { da, de, enUS, es, fr, he, pt, ru, uk } from 'date-fns/locale'
import dayjs from 'dayjs'
import i18n from 'i18n'
import React, { useEffect, useState } from 'react'
import {
  DateRange,
  DayPicker,
  rangeIncludesDate,
  useDayPicker
} from 'react-day-picker'
import Logger from 'services/Logger'
import { CalendarRange } from 'types/components/calendar'

const dayPickerLocales = {
  en: enUS,
  ru: ru,
  uk: uk,
  fr: fr,
  de: de,
  he: he,
  pt: pt,
  es: es,
  da: da
}

interface ViewDatesPickerProps {
  mode: 'single' | 'range'
  onSelect?: (selected: Date | DateRange | undefined) => void
  children?: React.ReactNode
  defaultRange?: DateRange
  defaultDate?: Date
  withoutSelection?: boolean
}

const ViewDatesPicker: React.FC<ViewDatesPickerProps> = (
  props: ViewDatesPickerProps
) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget)
  }

  const handleClose = () => {
    setAnchorEl(null)
  }

  const open = Boolean(anchorEl)
  const id = open ? 'date-picker-popover' : undefined

  return (
    <Box>
      <div onClick={handleClick}>{props.children}</div>
      <Popover
        slotProps={{
          paper: {
            sx: {
              borderRadius: '16px',
              boxShadow:
                '0px 4px 20px rgba(0, 0, 0, 0.15), 0px 8px 40px rgba(0, 0, 0, 0.3)'
            }
          }
        }}
        id={id}
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'center'
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'center'
        }}
      >
        <DateRangePicker
          {...props}
          onSelect={(selected) => {
            if (selected instanceof Date) handleClose()
            else if (
              selected &&
              selected.from &&
              selected.to &&
              selected.from.valueOf() != selected.to.valueOf()
            )
              handleClose()
            if (props.onSelect) props.onSelect(selected)
          }}
        />
      </Popover>
    </Box>
  )
}

export function DateRangePicker(props: ViewDatesPickerProps) {
  const calendar = useCalendar()

  const startDayJs = dayjs(calendar.fromISO)
  const endDayJs = dayjs(
    props.mode === 'single' ? calendar.fromISO : calendar.toISO
  )

  const centerDayJs = startDayJs.startOf('month').add(2, 'week')

  const [selectedDate, setSelectedDate] = useState<Date | undefined>(
    props.withoutSelection ? undefined : props.defaultDate
  )
  const [selectedRange, setSelectedRange] = useState<DateRange | undefined>(
    props.defaultRange
  )
  const [selectedMonth, setSelectedMonth] = useState<Date | undefined>(
    centerDayJs.toDate()
  )

  const postResult = (selected: Date | DateRange | undefined) => {
    if (props.onSelect) props.onSelect(selected)
  }
  const appSettings = useAppSettings()

  useEffect(() => {
    if (props.defaultRange) setSelectedRange(props.defaultRange)
    setSelectedMonth(
      dayjs(
        ((props.defaultRange?.from?.valueOf() ?? 0) +
          (props.defaultRange?.to?.valueOf() ?? 0)) /
          2
      ).toDate()
    )
  }, [props.defaultRange])

  useEffect(() => {
    if (props.defaultDate)
      setSelectedDate(props.withoutSelection ? undefined : props.defaultDate)
  }, [props.defaultDate])

  Logger.blue('ViewDatesPicker:CenterDayJS', selectedMonth)

  return (
    <Box p={2}>
      {props.mode === 'single' ? (
        <DayPicker
          components={{
            NextMonthButton: NextMonthButton,
            PreviousMonthButton: PreviousMonthButton
          }}
          captionLayout="dropdown"
          locale={
            dayPickerLocales[i18n.language as keyof typeof dayPickerLocales]
          }
          startMonth={new Date(dayjs().get('year') - 10, 0)}
          endMonth={new Date(dayjs().get('year') + 10, 0)}
          weekStartsOn={appSettings.firstDayOfTheWeek === 1 ? 0 : 1}
          defaultMonth={centerDayJs.toDate()}
          month={selectedMonth}
          onMonthChange={(month) => {
            setSelectedMonth(month)
          }}
          showOutsideDays
          mode="single"
          selected={selectedDate}
          onSelect={(date) => {
            setSelectedDate(date)
            postResult(date)
          }}
        />
      ) : (
        <DayPicker
          captionLayout="dropdown"
          locale={
            dayPickerLocales[i18n.language as keyof typeof dayPickerLocales]
          }
          components={{
            NextMonthButton: NextMonthButton,
            PreviousMonthButton: PreviousMonthButton
          }}
          startMonth={new Date(dayjs().get('year') - 10, 0)}
          endMonth={new Date(dayjs().get('year') + 10, 0)}
          defaultMonth={centerDayJs.toDate()}
          month={selectedMonth}
          onMonthChange={(month) => {
            setSelectedMonth(month)
          }}
          weekStartsOn={appSettings.firstDayOfTheWeek === 1 ? 0 : 1}
          showOutsideDays
          modifiers={{
            selected: selectedRange,
            range_start: selectedRange?.from,
            range_end: selectedRange?.to,
            range_middle: (date: Date) =>
              selectedRange
                ? rangeIncludesDate(selectedRange, date, false)
                : false
          }}
          onDayClick={(day, modifiers) => {
            if (modifiers.selected) {
              return
            }
            const selected = {
              from: startOfWeek(day, {
                weekStartsOn: appSettings.firstDayOfTheWeek === 1 ? 0 : 1
              }),
              to: endOfWeek(day, {
                weekStartsOn: appSettings.firstDayOfTheWeek === 1 ? 0 : 1
              })
            }
            setSelectedRange(selected)
            postResult(selected)
          }}
        />
      )}
    </Box>
  )
}

export default ViewDatesPicker

function NextMonthButton() {
  const daypicker = useDayPicker()
  const onClick = () => {
    if (daypicker.nextMonth) daypicker.goToMonth(daypicker.nextMonth)
  }
  return (
    <span id="next-button" onClick={onClick} style={{ cursor: 'pointer' }}>
      <CaretDownIcon
        style={{
          margin: 8,
          transform: i18n.dir() === 'ltr' ? 'rotate(-90deg)' : 'rotate(90deg)'
        }}
      />
    </span>
  )
}

function PreviousMonthButton() {
  const daypicker = useDayPicker()
  const onClick = () => {
    if (daypicker.previousMonth) daypicker.goToMonth(daypicker.previousMonth)
  }
  return (
    <span id="prev-button" onClick={onClick} style={{ cursor: 'pointer' }}>
      <CaretDownIcon
        style={{
          margin: 8,
          transform: i18n.dir() === 'ltr' ? 'rotate(90deg)' : 'rotate(-90deg)'
        }}
      />
    </span>
  )
}
